import {
    ActivityCategory,
    GoalMetricItemLink,
    TaskTypeId,
    TouchpointSourceContext,
    VoiceCallDirection,
} from "@sp-crm/core";
import { formatCallDuration } from "components/messages/call-duration";
import { addDays, format, subDays } from "date-fns";

export type ActivitySource = {
    [Kind in TouchpointSourceContext["kind"]]: { kind: Kind } & Partial<
        Omit<Extract<TouchpointSourceContext, { kind: Kind }>, "kind">
    >;
}[TouchpointSourceContext["kind"]];

export interface MyActivityItem {
    id: string;
    category: ActivityCategory;
    taskTypeId: TaskTypeId | null;
    date: string;
    time: string;
    parentName: string | null;
    link: GoalMetricItemLink | null;
    source: ActivitySource | null;
}

export interface MyActivityDay {
    date: string;
    items: MyActivityItem[];
}

export interface MyActivityRange {
    startDate: string;
    endDate: string;
}

export interface ActivityPresentation {
    summary: string | null;
    outcome: string | null;
}

const trailingDays = 30;

export const trailingMonthRange = (today: Date): MyActivityRange => ({
    startDate: format(subDays(today, trailingDays), "yyyy-MM-dd"),
    endDate: format(addDays(today, 1), "yyyy-MM-dd"),
});

const noonHour = 12;

export const formatActivityTime = (time: string): string => {
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours, 10);
    const meridiem = hour < noonHour ? "AM" : "PM";
    const displayHour = hour % noonHour === 0 ? noonHour : hour % noonHour;
    return `${displayHour}:${minutes} ${meridiem}`;
};

const snippetLength = 140;

const snippet = (text: string | null | undefined): string | null => {
    const collapsed = (text ?? "").replace(/\s+/g, " ").trim();
    if (!collapsed) {
        return null;
    }
    return collapsed.length > snippetLength
        ? `${collapsed.slice(0, snippetLength).trimEnd()}…`
        : collapsed;
};

const callDirectionLabels: Record<VoiceCallDirection, string> = {
    [VoiceCallDirection.Inbound]: "Inbound",
    [VoiceCallDirection.Outbound]: "Outbound",
};

const summarized = (summary: string | null): ActivityPresentation => ({
    summary,
    outcome: null,
});

const joinParts = (parts: (string | null)[]): string | null => {
    const present = parts.filter((part): part is string => Boolean(part));
    return present.length > 0 ? present.join(" · ") : null;
};

const describeCall = (
    direction: VoiceCallDirection | null | undefined,
    recordingDurationSec: number | null | undefined,
): string | null =>
    joinParts([
        direction ? callDirectionLabels[direction] : null,
        recordingDurationSec === null || recordingDurationSec === undefined
            ? null
            : formatCallDuration(recordingDurationSec),
    ]);

export const describeActivity = (item: MyActivityItem): ActivityPresentation => {
    const source = item.source;
    if (!source) {
        return summarized(null);
    }
    const kind = source.kind;
    switch (kind) {
        case "sentMessage":
            return summarized(snippet(source.subject) ?? "(no subject)");
        case "smsMessage":
            return summarized(snippet(source.body));
        case "voiceCall":
            return summarized(
                describeCall(source.direction, source.recordingDurationSec),
            );
        case "calendarEvent":
            return {
                summary: snippet(source.title),
                outcome: snippet(source.outcome),
            };
        case "task":
            return summarized(snippet(source.name));
        default: {
            const exhaustiveCheck: never = kind;
            throw new Error(`Unhandled touchpoint source kind: ${exhaustiveCheck}`);
        }
    }
};

export const groupActivityByDate = (items: MyActivityItem[]): MyActivityDay[] => {
    const days: MyActivityDay[] = [];
    const byDate = new Map<string, MyActivityDay>();
    for (const item of items) {
        const existing = byDate.get(item.date);
        const day = existing ?? { date: item.date, items: [] };
        if (!existing) {
            byDate.set(item.date, day);
            days.push(day);
        }
        day.items.push(item);
    }
    return days;
};
